import { useStyleRegister } from '@ant-design/cssinjs';
import { Button, ConfigProvider, DatePicker, Flex, theme } from 'antd';
import PropTypes from 'prop-types';
import { useContext, useRef, useState } from 'react';
import usePopupTriggerWidth from '@/common/hooks/use-popup-trigger-width';

const DATE_FORMAT = 'YYYY-MM-DD';
const DATE_RANGE_POPUP_CLASS_NAME = 'operation-analysis-date-range-popup';
const { RangePicker } = DatePicker;

const getDateRangePopupStyles = ({ pickerCls }) => ({
  [`.${DATE_RANGE_POPUP_CLASS_NAME}`]: {
    [`${pickerCls}-panel-layout`]: {
      flexDirection: 'column',
    },
    [`${pickerCls}-panels`]: {
      width: '100%',
      [`> ${pickerCls}-panel`]: {
        width: '100%',
      },
      [`> ${pickerCls}-panel + ${pickerCls}-panel`]: {
        display: 'none',
      },
      [`> ${pickerCls}-panel:first-child ${pickerCls}-header-next-btn, > ${pickerCls}-panel:first-child ${pickerCls}-header-super-next-btn`]:
        {
          visibility: 'visible !important',
        },
    },
    [`${pickerCls}-date-panel, ${pickerCls}-week-panel, ${pickerCls}-month-panel, ${pickerCls}-quarter-panel, ${pickerCls}-year-panel, ${pickerCls}-decade-panel`]:
      {
        width: '100%',
      },
    [`${pickerCls}-content`]: {
      width: '100%',
    },
  },
});

const MetricDateRangeFilter = ({
  ariaLabel,
  minDate,
  placement,
  presets = [],
  value,
  onChange,
}) => {
  const pickerRef = useRef(null);
  const popupWidth = usePopupTriggerWidth(pickerRef);
  const [open, setOpen] = useState(false);
  const { getPrefixCls } = useContext(ConfigProvider.ConfigContext);
  const { hashId, theme: designTheme, token } = theme.useToken();
  const pickerCls = `.${getPrefixCls('picker')}`;

  useStyleRegister(
    {
      path: ['MetricDateRangeFilter', hashId],
      theme: designTheme,
      token,
    },
    () => getDateRangePopupStyles({ pickerCls }),
  );

  const renderPresetFooter = () =>
    presets.length ? (
      <Flex gap="small" justify="center" wrap>
        {presets.map((preset) => (
          <Button
            key={String(preset.label)}
            size="medium"
            type="text"
            onClick={() => {
              const nextRange = preset.value();
              onChange(
                nextRange,
                nextRange.map((date) => date.format(DATE_FORMAT)),
              );
              setOpen(false);
            }}
          >
            {preset.label}
          </Button>
        ))}
      </Flex>
    ) : null;

  return (
    <RangePicker
      ref={pickerRef}
      allowClear
      aria-label={ariaLabel}
      classNames={{ popup: { root: DATE_RANGE_POPUP_CLASS_NAME } }}
      format={DATE_FORMAT}
      inputReadOnly
      minDate={minDate}
      open={open}
      placement={placement}
      placeholder={['开始日期', '结束日期']}
      renderExtraFooter={renderPresetFooter}
      styles={{
        popup: {
          root: {
            maxWidth: 'calc(100vw - 16px)',
            width: popupWidth,
          },
        },
        root: { width: '100%' },
      }}
      value={value}
      onChange={onChange}
      onOpenChange={setOpen}
    />
  );
};

MetricDateRangeFilter.propTypes = {
  ariaLabel: PropTypes.string.isRequired,
  minDate: PropTypes.shape({ format: PropTypes.func.isRequired }),
  placement: PropTypes.oneOf(['bottomLeft', 'bottomRight', 'topLeft', 'topRight']),
  presets: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      value: PropTypes.func.isRequired,
    }),
  ),
  value: PropTypes.arrayOf(PropTypes.shape({ format: PropTypes.func.isRequired })),
  onChange: PropTypes.func.isRequired,
};

export default MetricDateRangeFilter;
